// ЗВЁЗДЫ.2 (Этап 76): полноэкранная «рулетка изъятия» карт за Stars-рефанд.
//
// Показывается при заходе в игру, если сервер вернул players.pending_stars_
// clawback (изъятия за рефанд открытого ШК). Карты УЖЕ отняты на сервере —
// это визуализация: «Чумба, ты сделал возврат Звёздочек. Давай-ка мы тоже
// проведём возврат карточек.» → ленты прокручиваются и тормозят на тех
// картах, что забрали.
//
// Самодостаточный RAF (не Stage) — нужен точный контроль остановки каждой
// ленты. Карты в ленте — лёгкие чипы (рамка по редкости + имя), полный
// GameCard в ленте из десятков карт тяжёл для мобильного.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getCardData, cardIdsByRarity } from '../lib/cardData';
import { play } from '../audio/sfx';
import { useTr } from '../i18n/useTr';

export interface ClawbackCardInfo {
  id: string;
  name_ru: string;
  rarity: string;
  faction: string;
}
export interface ClawbackEntry {
  stars: number;
  at: string;
  cards: ClawbackCardInfo[];
}

const RARITY_COLOR: Record<string, string> = {
  common: '#A8AEBE', rare: '#3DF0FF', epic: '#C93DFF', legendary: '#FFB13D', special: '#A8AEBE'
};
const FACTION_COLOR: Record<string, string> = {
  deus: '#3D7DFF', syntetics: '#C93DFF', global: '#FF8A1F', livlife: '#3DFFA5', neutral: '#A0A4B4'
};

const CHIP_W = 72;
const CHIP_H = 96;
const GAP = 6;
const STEP = CHIP_W + GAP;
const STOP_INDEX = 22;       // позиция забираемой карты в ленте
const STRIP_LEN = 30;        // всего чипов в ленте
const ROW_DUR = 2.2;         // сек прокрутки одной ленты
const ROW_STAGGER = 0.45;    // задержка старта между лентами
const INTRO_DUR = 2.6;       // сек на текст до старта рулетки

function easeOutQuart(t: number): number { const u = t - 1; return 1 - u * u * u * u; }

// Лёгкий чип карты для ленты: арт /img/cards/<id>.jpg + рамка по редкости +
// имя снизу. Если арта нет (404) — фракционный градиент-заглушка.
function CardChip({ id, dim, highlight }: { id: string; dim?: boolean; highlight?: boolean }) {
  const c = getCardData(id);
  const rc = RARITY_COLOR[c?.rarity || 'common'] || '#A8AEBE';
  const fc = FACTION_COLOR[c?.faction || 'neutral'] || '#888';
  const [imgOk, setImgOk] = useState(true);
  return (
    <div style={{
      width: CHIP_W, height: CHIP_H, flex: `0 0 ${CHIP_W}px`,
      boxSizing: 'border-box',
      border: `1.5px solid ${rc}`,
      borderRadius: 4,
      background: `linear-gradient(160deg, ${fc}22, #0A0A12)`,
      boxShadow: highlight ? `0 0 18px ${rc}, inset 0 0 0 1px ${rc}` : 'none',
      opacity: dim ? 0.42 : 1,
      position: 'relative', overflow: 'hidden'
    }}>
      {imgOk && (
        <img
          src={`/img/cards/${id}.jpg`}
          alt=""
          loading="lazy"
          onError={() => setImgOk(false)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
        />
      )}
      {/* Затемнение снизу под подпись */}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        background: 'linear-gradient(transparent, rgba(5,5,9,0.92))',
        padding: '12px 4px 4px', textAlign: 'center'
      }}>
        <div style={{
          fontSize: 8, lineHeight: 1.1, color: '#F4F6FB', fontWeight: 700,
          maxHeight: 20, overflow: 'hidden'
        }}>
          {c?.name_ru || id}
        </div>
        <div style={{ fontSize: 6, letterSpacing: '0.06em', color: rc, textTransform: 'uppercase', fontWeight: 700 }}>
          {c?.rarity || ''}
        </div>
      </div>
    </div>
  );
}

// Одна лента: наполнитель нужной редкости + забираемая на STOP_INDEX.
function Strip({ card, rowIndex, containerW, onStopped }: {
  card: ClawbackCardInfo; rowIndex: number; containerW: number; onStopped: () => void;
}) {
  const ids = useMemo(() => {
    const filler = cardIdsByRarity(card.rarity);
    const pick = () => filler.length
      ? filler[Math.floor((rowIndex * 7.31 + Math.random() * filler.length)) % filler.length]
      : card.id;
    const arr: string[] = [];
    for (let i = 0; i < STRIP_LEN; i++) arr.push(i === STOP_INDEX ? card.id : pick());
    return arr;
  }, [card, rowIndex]);

  const [tx, setTx] = useState<number>(containerW);
  const rafRef = useRef<number | null>(null);
  const stoppedRef = useRef(false);

  useEffect(() => {
    // Финальное смещение: центр забираемой карты под маркером (центр контейнера).
    const txFinal = containerW / 2 - (STOP_INDEX * STEP + CHIP_W / 2);
    const txStart = containerW + STEP * 4; // лента стартует за правым краем
    const delay = rowIndex * ROW_STAGGER;
    let start: number | null = null;
    const tick = (ts: number) => {
      if (start === null) start = ts;
      const t = (ts - start) / 1000 - delay;
      if (t < 0) { rafRef.current = requestAnimationFrame(tick); return; }
      const p = Math.min(1, t / ROW_DUR);
      setTx(txStart + (txFinal - txStart) * easeOutQuart(p));
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else if (!stoppedRef.current) {
        stoppedRef.current = true;
        play('clockTick');
        onStopped();
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerW]);

  return (
    <div style={{ position: 'relative', height: CHIP_H, overflow: 'hidden', width: '100%' }}>
      <div style={{
        display: 'flex', gap: GAP, position: 'absolute', left: 0, top: 0,
        transform: `translateX(${tx}px)`, willChange: 'transform'
      }}>
        {ids.map((id, i) => (
          <CardChip key={i} id={id} dim={i !== STOP_INDEX} highlight={i === STOP_INDEX && stoppedRef.current} />
        ))}
      </div>
    </div>
  );
}

export function ClawbackScene({ entry, onDone }: { entry: ClawbackEntry; onDone: () => void }) {
  const tr = useTr();
  const cards = entry.cards || [];
  const [phase, setPhase] = useState<'intro' | 'roll' | 'done'>('intro');
  const [stoppedCount, setStoppedCount] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [containerW, setContainerW] = useState(0);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => { const r = el.getBoundingClientRect(); if (r.width > 0) setContainerW(r.width); };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Intro → roll.
  useEffect(() => {
    play('debuff');
    const id = setTimeout(() => setPhase('roll'), INTRO_DUR * 1000);
    return () => clearTimeout(id);
  }, []);

  // Когда все ленты остановились — финал.
  useEffect(() => {
    if (phase === 'roll' && cards.length > 0 && stoppedCount >= cards.length) {
      const id = setTimeout(() => { play('heroDeath'); setPhase('done'); }, 500);
      return () => clearTimeout(id);
    }
  }, [phase, stoppedCount, cards.length]);

  const node = (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 600,
      background: 'radial-gradient(ellipse 70% 60% at 50% 40%, rgba(80,10,20,0.55), #050509 75%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: 16, overflow: 'hidden'
    }}>
      {/* Заголовок */}
      <div style={{
        textAlign: 'center', marginBottom: 18, maxWidth: 560,
        opacity: phase === 'intro' ? 1 : 0.9,
        transition: 'opacity 0.4s'
      }}>
        <div style={{
          fontFamily: 'JetBrains Mono, monospace', fontSize: 11, letterSpacing: '0.28em',
          color: '#FF3D3D', textTransform: 'uppercase', textShadow: '0 0 12px #FF3D3D', marginBottom: 8
        }}>
          ⟢ ВОЗВРАТ КАРТОЧЕК ⟣
        </div>
        <div style={{
          fontFamily: 'Chakra Petch, sans-serif', fontSize: 17, fontWeight: 700,
          color: '#F4F6FB', lineHeight: 1.4
        }}>
          {tr(
            'Чумба, ты сделал возврат Звёздочек. Давай-ка мы тоже проведём возврат карточек.',
            'Choom, you refunded your Stars. So let us refund some cards too.'
          )}
        </div>
      </div>

      {/* Рулетки */}
      <div ref={wrapRef} style={{ width: '100%', maxWidth: 620, position: 'relative' }}>
        {/* Центральный маркер изъятия */}
        {phase !== 'intro' && (
          <div style={{
            position: 'absolute', left: '50%', top: -6, bottom: 40, width: 2,
            transform: 'translateX(-50%)', zIndex: 5,
            background: 'linear-gradient(#FF3D3D, transparent)', pointerEvents: 'none'
          }} />
        )}
        {phase !== 'intro' && containerW > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {cards.map((c, i) => (
              <Strip key={i} card={c} rowIndex={i} containerW={containerW}
                onStopped={() => setStoppedCount(n => n + 1)} />
            ))}
          </div>
        )}
      </div>

      {/* Финал */}
      {phase === 'done' && (
        <div style={{ textAlign: 'center', marginTop: 22 }}>
          <div style={{ color: '#FF7A7A', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
            {tr(`Изъято карт: ${cards.length}`, `Cards taken: ${cards.length}`)}
          </div>
          <button
            onClick={onDone}
            style={{
              padding: '12px 28px', background: '#FF3D3D', color: '#0A0A12',
              border: 'none', fontFamily: 'Chakra Petch, sans-serif', fontWeight: 700,
              fontSize: 13, letterSpacing: '0.1em', cursor: 'pointer', borderRadius: 3
            }}
          >
            {tr('ПОНЯЛ', 'GOT IT')}
          </button>
        </div>
      )}
    </div>
  );

  return createPortal(node, document.body);
}
